import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { uploadMultipleImagesToBackend } from "../../api/backendUploadService";

export interface Product {
  id?: string;
  name: string;
  description: string;
  price: number;
  stock: number;
  category: string;
  images: (File | string)[];
  materials?: string;
  size?: string;
  idealFor?: string;
  isFeaturedProduct?: boolean;
  enableMaterialType?: boolean;
}

const Container = styled.div`
  max-width: 700px;
  margin: 3rem auto;
  background: #fff;
  border-radius: 1.2rem;
  box-shadow: 0 2px 16px rgba(233, 125, 125, 0.09);
  padding: 2.5rem 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #e97d7d;
  margin-bottom: 2rem;
`;
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;
const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;
const Label = styled.label`
  font-weight: 500;
  color: #e97d7d;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: 0.7rem;
  border: 1.5px solid #e0e0e0;
  font-size: 1rem;
  background: #fafafa;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: 0.7rem;
  border: 1.5px solid #e0e0e0;
  font-size: 1rem;
  background: #fafafa;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #e97d7d;
  }
`;
const TextArea = styled.textarea`
  width: 100%;
  padding: 0.7rem 1rem;
  border-radius: 0.7rem;
  border: 1.5px solid #e0e0e0;
  font-size: 1rem;
  background: #fafafa;
`;
const ButtonRow = styled.div`
  display: flex;
  gap: 1.2rem;
  margin-top: 2.2rem;
`;
const Button = styled.button`
  background: #e97d7d;
  color: #fff;
  border: none;
  border-radius: 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  padding: 0.9rem 2.2rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #d96a6a;
  }
`;
const Thumbnails = styled.div`
  display: flex;
  gap: 0.7rem;
  margin-top: 0.7rem;
`;
const Thumbnail = styled.img`
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 0.7rem;
  border: 2px solid #e0e0e0;
  background: #fafafa;
`;

const ThumbnailContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #e97d7d;
  color: white;
  border: none;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover {
    background: #d65a5a;
  }
`;

const ImageCount = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const Checkbox = styled.input`
  width: auto;
  margin: 0;
  accent-color: #e97d7d;
`;

interface ProductFormProps {
  product?: Product;
  onSave: (data: Product) => void;
  onCancel?: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({
  product,
  onSave,
  onCancel,
}) => {
  const [form, setForm] = useState<Product>({
    id: product?.id,
    name: product?.name || "",
    description: product?.description || "",
    price: product?.price || 0,
    stock: product?.stock || 0,
    category: product?.category || "",
    images: [],
    materials: product?.materials || "",
    size: product?.size || "",
    idealFor: product?.idealFor || "",
    isFeaturedProduct: product?.isFeaturedProduct || false,
    enableMaterialType: product?.enableMaterialType || false,
  });
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (product && product.images && product.images.length > 0) {
      // If editing, show existing image previews (URLs)
      setImagePreviews(
        product.images.map((img) => (typeof img === "string" ? img : ""))
      );
    }
  }, [product]);

  useEffect(() => {
    // Generate previews for selected files
    if (imageFiles.length > 0) {
      const previews = imageFiles.map((file) => URL.createObjectURL(file));
      setImagePreviews(previews);
      return () => previews.forEach((url) => URL.revokeObjectURL(url));
    } else if (product && product.images && product.images.length > 0) {
      // Show existing image URLs
      setImagePreviews(
        product.images.map((img) => (typeof img === "string" ? img : ""))
      );
    }
  }, [imageFiles, product]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setForm((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "price" || name === "stock"
          ? Number(value)
          : value,
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      // Limit to 10 images maximum
      const limitedFiles = files.slice(0, 10);
      setImageFiles(limitedFiles);
      setForm((prev) => ({ ...prev, images: limitedFiles }));

      if (files.length > 10) {
        alert("Maximum 10 images allowed. Only the first 10 will be uploaded.");
      }
    }
  };

  const removeImage = (index: number) => {
    const newFiles = imageFiles.filter((_, i) => i !== index);
    setImageFiles(newFiles);
    setForm((prev) => ({ ...prev, images: newFiles }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setUploading(true);
      let imageUrls: string[] = [];

      // If there are new image files to upload
      if (imageFiles.length > 0) {
        // For new products, we'll use a temporary ID
        const tempId = product?.id || `temp_${Date.now()}`;
        imageUrls = await uploadMultipleImagesToBackend(imageFiles, tempId);
      } else if (product?.images && product.images.length > 0) {
        // For existing products, keep existing image URLs
        imageUrls = product.images.map((img) =>
          typeof img === "string" ? img : ""
        );
      }

      // Update form with image URLs instead of File objects
      const productData = {
        ...form,
        images: imageUrls,
      };

      onSave(productData);
    } catch (error) {
      console.error("Error uploading images:", error);
      alert("Failed to upload images. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container>
      <Title>{product ? "Edit Product" : "Add New Product"}</Title>
      <Form onSubmit={handleSubmit} autoComplete="off">
        <Field>
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
          />
        </Field>
        <Field>
          <Label htmlFor="description">Description</Label>
          <TextArea
            id="description"
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={3}
          />
        </Field>
        <Field>
          <Label htmlFor="price">Price</Label>
          <Input
            id="price"
            name="price"
            type="number"
            value={form.price}
            onChange={handleChange}
            required
            min={0}
            step={0.01}
          />
        </Field>
        <Field>
          <Label htmlFor="stock">Stock</Label>
          <Input
            id="stock"
            name="stock"
            type="number"
            value={form.stock}
            onChange={handleChange}
            required
            min={0}
            step={1}
          />
        </Field>
        <Field>
          <Label htmlFor="category">Category</Label>
          <Select
            id="category"
            name="category"
            value={form.category}
            onChange={handleChange}
            required
          >
            <option value="">Select a category</option>
            <option value="Resin Jewelry">Resin Jewelry</option>
            <option value="Pet Tags">Pet Tags</option>
            <option value="Embroidery">Embroidery</option>
            <option value="Accessories">Accessories</option>
          </Select>
        </Field>
        <Field>
          <Label htmlFor="materials">Materials</Label>
          <Input
            id="materials"
            name="materials"
            value={form.materials || ""}
            onChange={handleChange}
            placeholder="e.g., Sterling silver, 14k gold, gemstones"
          />
        </Field>
        <Field>
          <Label htmlFor="size">Size</Label>
          <Input
            id="size"
            name="size"
            value={form.size || ""}
            onChange={handleChange}
            placeholder="e.g., Small (6-7), Medium (7-8), Large (8-9)"
          />
        </Field>
        <Field>
          <Label htmlFor="idealFor">Ideal For</Label>
          <Input
            id="idealFor"
            name="idealFor"
            value={form.idealFor || ""}
            onChange={handleChange}
            placeholder="e.g., Daily wear, special occasions, gifts"
          />
        </Field>
        <Field>
          <CheckboxContainer>
            <Checkbox
              id="isFeaturedProduct"
              name="isFeaturedProduct"
              type="checkbox"
              checked={form.isFeaturedProduct || false}
              onChange={handleChange}
            />
            <Label htmlFor="isFeaturedProduct">Featured Product</Label>
          </CheckboxContainer>
        </Field>
        <Field>
          <CheckboxContainer>
            <Checkbox
              id="enableMaterialType"
              name="enableMaterialType"
              type="checkbox"
              checked={form.enableMaterialType || false}
              onChange={handleChange}
            />
            <Label htmlFor="enableMaterialType">Enable Material Type</Label>
          </CheckboxContainer>
        </Field>
        <Field>
          <Label htmlFor="images">Images</Label>
          <Input
            id="images"
            name="images"
            type="file"
            multiple
            accept="image/*"
            ref={fileInputRef}
            onChange={handleImageChange}
          />
          {imagePreviews.length > 0 && (
            <Thumbnails>
              {imagePreviews.map((src, idx) =>
                src ? (
                  <ThumbnailContainer key={idx}>
                    <Thumbnail src={src} alt={`Preview ${idx + 1}`} />
                    <RemoveButton onClick={() => removeImage(idx)}>
                      ×
                    </RemoveButton>
                  </ThumbnailContainer>
                ) : null
              )}
            </Thumbnails>
          )}
          {imageFiles.length > 0 && (
            <ImageCount>
              {imageFiles.length} image{imageFiles.length !== 1 ? "s" : ""}{" "}
              selected
            </ImageCount>
          )}
        </Field>
        {uploading && (
          <div
            style={{
              textAlign: "center",
              padding: "1rem",
              color: "#e97d7d",
              fontWeight: "600",
            }}
          >
            📤 Uploading {imageFiles.length} image
            {imageFiles.length !== 1 ? "s" : ""} to server...
          </div>
        )}
        <ButtonRow>
          <Button type="button" onClick={onCancel} disabled={uploading}>
            Cancel
          </Button>
          <Button type="submit" disabled={uploading}>
            {uploading ? "Uploading..." : product ? "Update" : "Save"}
          </Button>
        </ButtonRow>
      </Form>
    </Container>
  );
};

export default ProductForm;
